/**
 * Dance floor size decision logic, per PRD §10.
 *
 * The engine prefers the larger floor when guest count warrants it, but
 * steps down to the smaller floor if the room can't support it alongside
 * required guest seating at proper clearances. This is a deliberately
 * conservative estimate function — exact remaining-floor-area validation
 * happens later in the full placement + validation pass; this module just
 * makes the *initial* size choice the placement step will attempt first.
 */

import type { RoomDefinition } from './types';

export type DanceFloorSize = 'dance_floor_15' | 'dance_floor_18';

const PEAK_DANCER_RATIO = 0.4;
const DANCE_FLOOR_BUFFER_FT = 4;

export interface DanceFloorDecision {
  size: DanceFloorSize;
  estimatedPeakDancers: number;
  steppedDown: boolean;
}

/**
 * Rough "does the 18x18 + its buffer even fit in this room at all" sanity
 * check — a cheap pre-filter before the full placement engine tries to fit
 * guest tables around it. This does not guarantee the full layout will
 * validate; it only prevents attempting an 18x18 in a room where it
 * obviously can't work (e.g. Tree Deck's narrower footprint).
 */
function eighteenFootFloorPhysicallyFits(room: RoomDefinition): boolean {
  const requiredFootprint = 18 + DANCE_FLOOR_BUFFER_FT * 2;
  return room.widthFt >= requiredFootprint || room.lengthFt >= requiredFootprint;
}

export function decideDanceFloorSize(
  guestCount: number,
  room: RoomDefinition
): DanceFloorDecision {
  const estimatedPeakDancers = Math.round(guestCount * PEAK_DANCER_RATIO);
  const preferLarger = guestCount > 150;

  if (!preferLarger) {
    return { size: 'dance_floor_15', estimatedPeakDancers, steppedDown: false };
  }

  if (eighteenFootFloorPhysicallyFits(room)) {
    return { size: 'dance_floor_18', estimatedPeakDancers, steppedDown: false };
  }

  return { size: 'dance_floor_15', estimatedPeakDancers, steppedDown: true };
}
